import { inject, Injectable } from '@angular/core';
import { Observable, of, switchMap } from 'rxjs';
import { catchError, first, map, tap } from 'rxjs/operators';
import { CountryDetectorService, ResidenceCurrencyService } from '@lx/quote-tool/shared/services';
import { LoggerService } from '@lx/shared/logger';
import { parseStringListFromConfigValue } from '@lx/shared/app-config';
import { OnlineQuoteToolStore } from '../store';
import { Configuration } from '@lx/shared/types';
import { ResidenceCountryService } from '@lx/shared/data-access/destination';

/**
 * Service responsible for initializing country and currency for the Online Quote Tool.
 *
 * This service detects the user's country from their browser timezone and fetches
 * the appropriate currency for that country. The data is then stored in the application state.
 */
@Injectable({
  providedIn: 'root',
})
export class CountryCurrencyInitializerService {
  private readonly DEFAULT_CURRENCY = 'USD';
  private readonly countryDetectorService = inject(CountryDetectorService);
  private readonly residenceCurrencyService = inject(ResidenceCurrencyService);
  private readonly residenceCountryService = inject(ResidenceCountryService);
  private readonly store = inject(OnlineQuoteToolStore);
  private readonly logger = inject(LoggerService);

  /**
   * Initializes country and currency in the store.
   *
   * Steps:
   * 1. Detects the user's country from browser timezone
   * 2. Extracts relevant configuration values
   * 3. Fetches residence countries to get phone code
   * 4. Fetches the currency for that country based on configuration rules
   * 5. Updates the store with all values
   *
   * If country detection fails, uses empty string and default currency (USD).
   * If currency fetch fails, uses default currency (USD).
   *
   * @param configuration - Application configuration containing currency selection rules
   * @returns Observable that completes when initialization is done
   */
  initializeCountryAndCurrency(configuration: Configuration): Observable<void> {
    const countryCode = this.countryDetectorService.detectCountryCode() ?? '';

    if (!countryCode) {
      this.logger.warn('Could not detect country from browser timezone, using default currency');
      this.updateStoreWithDefaults(countryCode);
      return of(undefined);
    }

    // Extract specific configuration values needed for currency selection
    const useMarketCurrency = configuration.USE_MARKET_CURRENCY as boolean;
    const useResidenceCurrencyCountries = parseStringListFromConfigValue(configuration.USE_RESIDENCE_CURRENCY_COUNTRIES);

    // First get residence countries to find phone code
    return this.residenceCountryService.getAll().pipe(
      first(),
      switchMap((countries) => {
        const country = countries.find((c) => c.code === countryCode);
        const phoneCode = country?.phoneCode;

        // Then get currency
        return this.residenceCurrencyService.getResidenceCurrency(countryCode, useMarketCurrency, useResidenceCurrencyCountries).pipe(
          first(),
          catchError((error) => {
            this.logger.error(`Failed to fetch currency for country ${countryCode}, using default`, error);
            return of(this.DEFAULT_CURRENCY);
          }),
          map((currency) => ({ currency, phoneCode })),
        );
      }),
      catchError((error) => {
        this.logger.error('Failed to fetch residence countries', error);
        return of({ currency: this.DEFAULT_CURRENCY, phoneCode: undefined });
      }),
      tap(({ currency, phoneCode }) => {
        this.store.updateResidenceData({
          residenceCountryCode: countryCode,
          phoneCode,
          currency,
        });
      }),
      map(() => undefined),
      catchError((error) => {
        this.logger.error('Unexpected error during currency initialization', error);
        this.updateStoreWithDefaults(countryCode);
        return of(undefined);
      }),
    );
  }

  /**
   * Updates store with default currency when initialization fails
   * @param countryCode - The detected country code (may be empty)
   * @private
   */
  private updateStoreWithDefaults(countryCode: string): void {
    this.store.updateResidenceData({
      residenceCountryCode: countryCode,
      currency: this.DEFAULT_CURRENCY,
    });
  }
}
